import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@tanstack/react-query';
import queryClient from '@/services/queryClient';
import { checkAuthWithRetry } from '@/utils/supabase/authCheck';
import { createClient } from '@/utils/supabase/client';
const supabase = createClient();
const queryKey = ['user'];

// Throwing on an indeterminate check is what makes this recoverable.
// Swallowing the error and returning `data.user` turned a transient Supabase
// blip into `null`, and consumers read `null` as "logged out" and
// `router.push('/login')` — so the client bounced researchers to the login
// screen even after the middleware stopped doing it (KBE-767).
//
// A thrown error is retried by `queryClient`'s `shouldRetry` (3 attempts,
// ~7s of backoff). `isLoading` stays true for that whole window — the
// `failed` action in query-core's reducer updates only `fetchFailureCount`,
// never `fetchStatus`, so it remains `'fetching'` between attempts and
// `isLoading` (`isPending && isFetching`) stays true. Consumers gating on
// `!isLoading && !user` therefore do not redirect during recovery.
//
// Once the retries are exhausted the query settles to `error`, `isLoading`
// goes false and those consumers do redirect. That is deliberate: at that
// point Supabase has been unreachable for several seconds, and /login is a
// more useful destination than the blank render `if (!user) return null`
// would otherwise give — the login page re-attempts session restore and
// returns the researcher to where they were once Supabase recovers.
const useUser = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      queryKey,
      queryFn: _temp,
      staleTime: Infinity,
      meta: {
        onError: _temp2
      }
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useQuery(t0);
};
supabase.auth.onAuthStateChange((event, session) => {
  if (!session?.user) {
    console.warn('onAuthStateChange: user logged out', event, session);
  }
  const user = session ? session.user : null;
  queryClient.setQueryData(queryKey, user);
});
export default useUser;
async function _temp() {
  const authCheck = await checkAuthWithRetry(supabase.auth);
  if (authCheck.kind === "indeterminate") {
    throw new Error(`Could not verify the session: ${authCheck.reason}`);
  }
  return authCheck.kind === "authenticated" ? authCheck.user : null;
}
function _temp2(error) {
  console.error("Session verification failed", error);
}